export class EntityRepository implements LsCloudEntityRepository {
  protected entities: Record<string, any>[] | null = null;
  protected type: LsCloudEntityType;
  private getAllPromise: Promise<Record<string, any>[]> | null = null;

  constructor(type: LsCloudEntityType) {
    this.type = type;
  }

  protected getAccountID(): string {
    const accountID = window.merchantos.session.getSystemCustomerID();
    if (!accountID) {
      throw new Error('Account ID not found. Session may not be initialized yet.');
    }
    return String(accountID);
  }

  protected getEmployeeID(): string {
    const employeeID = window.merchantos.session.getEmployee()?.employeeID;
    if (!employeeID) {
      throw new Error('Employee ID not found');
    }
    return employeeID;
  }

  protected async waitForSession(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (window.merchantos.session.getSystemCustomerID()) {
        resolve();
        return;
      }

      window.merchantos.session
        .init()
        .done(() => {
          if (window.merchantos.session.getSystemCustomerID()) {
            resolve();
          } else {
            reject(new Error('Session initialized but systemCustomerID still not available'));
          }
        })
        .fail((error) => {
          reject(new Error(`Session initialization failed: ${error}`));
        });
    });
  }

  protected getPath(): string {
    return `/API/Account/${this.getAccountID()}/${this.type}.json?`;
  }

  private async loadAll(
    offset = 0,
    limit = 100,
    entitiesAccumulator = []
  ): Promise<Record<string, any>[]> {
    try {
      const path = `${this.getPath()}&offset=${offset}&limit=${limit}`;
      const response = await window.lsCloud.getJSONFromAPI(path);
      if (!response[this.type]) {
        return entitiesAccumulator;
      }
      if (!Array.isArray(response[this.type])) {
        response[this.type] = [response[this.type]];
      }
      entitiesAccumulator = entitiesAccumulator.concat(response[this.type]);
      if (response[this.type].length === limit) {
        return this.loadAll(offset + limit, limit, entitiesAccumulator);
      }
      return entitiesAccumulator;
    } catch (error) {
      throw error;
    }
  }

  public async getAll(
    options: LsCloudEntityRepositoryGetAllOptions = {}
  ): Promise<Record<string, any>[]> {
    try {
      if (this.entities && !options.reload) {
        return this.entities;
      }

      if (options.reload) {
        this.entities = null;
        this.getAllPromise = null;
      }

      if (this.getAllPromise) {
        return this.getAllPromise;
      }

      this.getAllPromise = this.performGetAllLoad();
      return this.getAllPromise;
    } catch (error) {
      throw error;
    }
  }

  private async performGetAllLoad(): Promise<Record<string, any>[]> {
    try {
      await this.waitForSession();
      const entities = await this.loadAll();
      this.entities = entities;
      return entities;
    } catch (error) {
      throw error;
    } finally {
      this.getAllPromise = null;
    }
  }

  public clearCache(): void {
    this.entities = null;
    this.getAllPromise = null;
  }
}
